import { FEATURE_CHARACTER_URLS } from '../rendering/asset-manifest';
import { FEATURE_SUMMARY_AUTO_CLOSE_MS, getFeatureSummaryTier } from '../presentation/feature-presentation';
import type { FeatureOverlayPort } from '../presentation/feature-presentation';
import type { GridPosition } from '../rendering/types';
import { animateCentCount } from './count-up';
import { formatCents } from './format';

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`Feature-Element fehlt: ${selector}`);
  return element;
}

export interface FeatureDialogOptions {
  readonly reducedMotion?: boolean;
  readonly onPotOfGreed?: (position: GridPosition) => void | Promise<void>;
}

export class FeatureDialogController implements FeatureOverlayPort {
  private readonly intro: HTMLDialogElement;
  private readonly award: HTMLOutputElement;
  private readonly summary: HTMLDialogElement;
  private readonly summaryFrame: HTMLElement;
  private readonly character: HTMLImageElement;
  private readonly eyebrow: HTMLElement;
  private readonly title: HTMLElement;
  private readonly total: HTMLOutputElement;
  private readonly copy: HTMLElement;
  private summaryTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

  constructor(root: Document, private readonly options: FeatureDialogOptions = {}) {
    this.intro = requireElement(root, '[data-feature-intro-dialog]');
    this.award = requireElement(root, '[data-feature-award]');
    this.summary = requireElement(root, '[data-feature-summary-dialog]');
    this.summaryFrame = requireElement(this.summary, '[data-feature-summary-tone]');
    this.character = requireElement(this.summary, '[data-feature-character]');
    this.eyebrow = requireElement(this.summary, '[data-feature-eyebrow]');
    this.title = requireElement(this.summary, '[data-feature-title]');
    this.total = requireElement(this.summary, '[data-feature-total]');
    this.copy = requireElement(this.summary, '[data-feature-copy]');
  }

  showIntro(awardedSpins: number): Promise<void> {
    this.award.value = String(Math.max(0, Math.trunc(awardedSpins)));
    if (!this.intro.open) this.intro.showModal();
    return this.waitForClose(this.intro);
  }

  async showRetrigger(awardedSpins: number): Promise<void> {
    const status = document.querySelector<HTMLElement>('[data-status-text]');
    if (status !== null) status.textContent = `Retrigger: +${Math.max(0, Math.trunc(awardedSpins))} Freispiele`;
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, this.options.reducedMotion === true ? 80 : 650));
  }

  async showPotOfGreed(position: GridPosition): Promise<void> {
    await this.options.onPotOfGreed?.(position);
  }

  async showSummary(totalWinCents: number, betCents: number): Promise<void> {
    const tier = getFeatureSummaryTier(totalWinCents, betCents);
    this.summaryFrame.dataset.featureSummaryTone = tier.tone;
    this.character.src = FEATURE_CHARACTER_URLS[tier.character];
    this.character.alt = tier.character === 'joey' ? 'Joey Wheeler'
      : tier.character === 'seto' ? 'Seto Kaiba'
        : tier.character === 'marik' ? 'Marik Ishtar' : 'Solomon Muto';
    this.eyebrow.textContent = tier.eyebrow;
    this.title.textContent = tier.title;
    this.copy.textContent = tier.copy;
    this.total.value = formatCents(0);
    if (!this.summary.open) this.summary.showModal();
    if (this.summaryTimer !== null) globalThis.clearTimeout(this.summaryTimer);
    this.summaryTimer = globalThis.setTimeout(() => {
      this.summaryTimer = null;
      if (this.summary.open) this.summary.close();
    }, FEATURE_SUMMARY_AUTO_CLOSE_MS);
    await animateCentCount({
      fromCents: 0,
      toCents: totalWinCents,
      durationMs: tier.countUpMs,
      reducedMotion: this.options.reducedMotion,
      onUpdate: (value) => { this.total.value = formatCents(value); },
    });
    await this.waitForClose(this.summary);
  }

  destroy(): void {
    if (this.summaryTimer !== null) globalThis.clearTimeout(this.summaryTimer);
    this.summaryTimer = null;
  }

  private waitForClose(dialog: HTMLDialogElement): Promise<void> {
    if (!dialog.open) return Promise.resolve();
    return new Promise((resolve) => dialog.addEventListener('close', () => resolve(), { once: true }));
  }
}
